import { useCallback, useEffect, useMemo, useState } from "react";
import { Card, Radio, Select } from "antd";
import { toast } from "react-hot-toast";
import Footer from "../components/Footer";
import Header from "../components/Header";
import { useTranslation } from "react-i18next";
import StandardButton from "../components/StandardButton";

const WORD_POOL = [
  "lorem", "ipsum", "dolor", "sit", "amet", "consectetur", "adipiscing", "elit",
  "sed", "do", "eiusmod", "tempor", "incididunt", "ut", "labore", "et", "dolore",
  "magna", "aliqua", "enim", "ad", "minim", "veniam", "quis", "nostrud",
  "exercitation", "ullamco", "laboris", "nisi", "aliquip", "ex", "ea", "commodo",
  "consequat", "duis", "aute", "irure", "in", "reprehenderit", "voluptate",
  "velit", "esse", "cillum", "fugiat", "nulla", "pariatur", "excepteur", "sint",
  "occaecat", "cupidatat", "non", "proident", "sunt", "culpa", "qui", "officia",
  "deserunt", "mollit", "anim", "id", "est", "laborum", "at", "vero", "eos",
  "accusamus", "iusto", "odio", "dignissimos", "ducimus", "blanditiis",
  "praesentium", "voluptatum", "deleniti", "atque", "corrupti", "quos",
  "quas", "molestias", "excepturi", "obcaecati", "cupiditate", "similique",
  "tempora", "incidunt", "magnam", "quaerat", "voluptatem", "fugit",
  "consequuntur", "vel", "quia", "voluptas", "aspernatur", "aut", "odit",
  "fugitque", "sequi", "nesciunt", "neque", "porro", "quisquam",
];

const CLASSIC_START = [
  "lorem", "ipsum", "dolor", "sit", "amet", "consectetur", "adipiscing", "elit",
];

const MODES = ["paragraphs", "sentences", "words"];
const FORMAT_PLAIN = "plain";
const FORMAT_HTML = "html";
const MIN_COUNT = 1;
const MAX_COUNT = 50;

const pickWord = () => WORD_POOL[Math.floor(Math.random() * WORD_POOL.length)];

const capitalize = (word) =>
  word.length === 0 ? word : word.charAt(0).toUpperCase() + word.slice(1);

const buildSentence = (isFirst, startClassic) => {
  const length = 6 + Math.floor(Math.random() * 11);
  const words = [];
  for (let i = 0; i < length; i += 1) {
    if (isFirst && startClassic && i < CLASSIC_START.length) {
      words.push(CLASSIC_START[i]);
    } else {
      words.push(pickWord());
    }
  }
  if (length > 6 && Math.random() < 0.4) {
    const commaAt = 2 + Math.floor(Math.random() * (length - 4));
    words[commaAt] = `${words[commaAt]},`;
  }
  words[0] = capitalize(words[0]);
  return `${words.join(" ")}.`;
};

const buildParagraph = (isFirstParagraph, startClassic) => {
  const sentenceCount = 3 + Math.floor(Math.random() * 4);
  const sentences = [];
  for (let i = 0; i < sentenceCount; i += 1) {
    sentences.push(buildSentence(isFirstParagraph && i === 0, startClassic));
  }
  return sentences.join(" ");
};

const buildWords = (count, startClassic) => {
  const words = [];
  for (let i = 0; i < count; i += 1) {
    if (startClassic && i < CLASSIC_START.length) {
      words.push(CLASSIC_START[i]);
    } else {
      words.push(pickWord());
    }
  }
  words[0] = capitalize(words[0]);
  return `${words.join(" ")}.`;
};

const buildSentences = (count, startClassic) => {
  const sentences = [];
  for (let i = 0; i < count; i += 1) {
    sentences.push(buildSentence(i === 0, startClassic));
  }
  return sentences.join(" ");
};

const buildParagraphs = (count, startClassic) => {
  const paragraphs = [];
  for (let i = 0; i < count; i += 1) {
    paragraphs.push(buildParagraph(i === 0, startClassic));
  }
  return paragraphs.join("\n\n");
};

const clampCount = (value) => {
  const n = Number.parseInt(value, 10);
  if (Number.isNaN(n)) return MIN_COUNT;
  return Math.min(MAX_COUNT, Math.max(MIN_COUNT, n));
};

const generateText = (mode, count, startClassic) => {
  if (mode === "words") return buildWords(count, startClassic);
  if (mode === "sentences") return buildSentences(count, startClassic);
  return buildParagraphs(count, startClassic);
};

const countWords = (text) =>
  text ? text.trim().split(/\s+/).filter(Boolean).length : 0;

const countChars = (text) => (text ? text.length : 0);

const wrapHtml = (text, mode) => {
  if (!text) return "";
  if (mode === "paragraphs") {
    return text
      .split(/\n{2,}/)
      .map((p) => `<p>${p}</p>`)
      .join("\n");
  }
  return `<p>${text}</p>`;
};

const formatOutput = (text, mode, outputFormat) =>
  outputFormat === FORMAT_HTML ? wrapHtml(text, mode) : text;

const LoremIpsumGenerator = () => {
  const { t } = useTranslation();
  const [mode, setMode] = useState("paragraphs");
  const [count, setCount] = useState(3);
  const [startClassic, setStartClassic] = useState(true);
  const [outputFormat, setOutputFormat] = useState(FORMAT_PLAIN);
  const [rawOutput, setRawOutput] = useState(() => buildParagraphs(3, true));
  const [outputMode, setOutputMode] = useState("paragraphs");

  const generate = useCallback(() => {
    const safeCount = clampCount(count);
    setRawOutput(generateText(mode, safeCount, startClassic));
    setOutputMode(mode);
  }, [mode, count, startClassic]);

  const displayOutput = useMemo(
    () => formatOutput(rawOutput, outputMode, outputFormat),
    [rawOutput, outputMode, outputFormat],
  );

  const stats = useMemo(
    () => ({ words: countWords(rawOutput), chars: countChars(displayOutput) }),
    [rawOutput, displayOutput],
  );

  const copy = async () => {
    if (!displayOutput) {
      toast.error(t("lorem_ipsum_nothing_to_copy"));
      return;
    }
    try {
      await navigator.clipboard.writeText(displayOutput);
      toast.success(t("copy_success"));
    } catch {
      toast.error(t("failed_to_copy"));
    }
  };

  const clear = () => setRawOutput("");

  useEffect(() => {
    const onKeyDown = (e) => {
      if ((e.metaKey || e.ctrlKey) && e.key === "Enter") {
        e.preventDefault();
        generate();
      }
    };
    window.addEventListener("keydown", onKeyDown);
    return () => window.removeEventListener("keydown", onKeyDown);
  }, [generate]);

  return (
    <div className="min-h-screen bg-gradient-to-b from-white via-indigo-50 to-white p-0 text-gray-800">
      <Header />
      <div className="max-w-5xl mx-auto p-4">
        <Card className="shadow-lg rounded-xl">
          <div className="p-4 flex flex-col gap-4">
            <h1 className="text-2xl font-bold text-indigo-700">
              {t("lorem_ipsum_title")}
            </h1>
            <p className="text-gray-600">{t("lorem_ipsum_intro")}</p>

            <div className="flex flex-wrap items-end gap-4">
              <div className="flex flex-col gap-1">
                <label className="text-sm font-medium text-gray-700">
                  {t("lorem_ipsum_mode_label")}
                </label>
                <Select
                  value={mode}
                  onChange={setMode}
                  style={{ width: 180 }}
                  options={MODES.map((m) => ({
                    value: m,
                    label: t(`lorem_ipsum_mode_${m}`),
                  }))}
                />
              </div>

              <div className="flex flex-col gap-1">
                <label className="text-sm font-medium text-gray-700">
                  {t("lorem_ipsum_count_label")}
                </label>
                <input
                  type="number"
                  min={MIN_COUNT}
                  max={MAX_COUNT}
                  value={count}
                  onChange={(e) => setCount(clampCount(e.target.value))}
                  className="w-32 p-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-indigo-400 focus:outline-none"
                />
              </div>

              <label className="flex items-center gap-2 text-sm text-gray-700 pb-2">
                <input
                  type="checkbox"
                  checked={startClassic}
                  onChange={(e) => setStartClassic(e.target.checked)}
                  className="h-4 w-4"
                />
                {t("lorem_ipsum_start_classic")}
              </label>

              <StandardButton
                onClick={generate}
                type="primary"
                size="middle"
                title={t("lorem_ipsum_shortcut_hint")}
              >
                {t("lorem_ipsum_generate")}
              </StandardButton>
            </div>

            <div className="flex flex-col gap-1">
              <label className="text-sm font-medium text-gray-700">
                {t("lorem_ipsum_format_label")}
              </label>
              <Radio.Group
                value={outputFormat}
                onChange={(e) => setOutputFormat(e.target.value)}
              >
                <Radio.Button value={FORMAT_PLAIN}>
                  {t("lorem_ipsum_format_plain")}
                </Radio.Button>
                <Radio.Button value={FORMAT_HTML}>
                  {t("lorem_ipsum_format_html")}
                </Radio.Button>
              </Radio.Group>
              <span className="text-xs text-gray-500">
                {t("lorem_ipsum_shortcut_hint")}
              </span>
            </div>

            <div className="flex flex-col gap-1">
              <div className="flex items-center justify-between">
                <label className="text-sm font-medium text-gray-700">
                  {t("lorem_ipsum_output_label")}
                </label>
                <div className="flex items-center gap-2">
                  <StandardButton
                    onClick={clear}
                    type="secondary"
                    variant="outlined"
                    size="small"
                    disabled={!rawOutput}
                  >
                    {t("lorem_ipsum_clear")}
                  </StandardButton>
                  <StandardButton
                    onClick={copy}
                    type="primary"
                    size="small"
                    disabled={!displayOutput}
                  >
                    {t("copy_button")}
                  </StandardButton>
                </div>
              </div>
              <pre
                className={`w-full min-h-[8rem] p-3 text-sm bg-gray-50 border border-gray-200 rounded-md whitespace-pre-wrap break-words text-gray-800 ${
                  outputFormat === FORMAT_HTML ? "font-mono" : "font-serif"
                }`}
              >
                {displayOutput || (
                  <em className="text-gray-400 not-italic">
                    {t("lorem_ipsum_output_empty")}
                  </em>
                )}
              </pre>
              <div className="text-xs text-gray-500">
                {t("lorem_ipsum_stats", {
                  words: stats.words,
                  chars: stats.chars,
                })}
              </div>
            </div>

            <div className="text-xs text-gray-500 leading-relaxed">
              <p>{t("lorem_ipsum_note_modes")}</p>
              <p>{t("lorem_ipsum_note_classic")}</p>
              <p>{t("lorem_ipsum_note_format")}</p>
              <p>{t("lorem_ipsum_note_privacy")}</p>
            </div>
          </div>
        </Card>
      </div>
      <Footer />
    </div>
  );
};

export default LoremIpsumGenerator;
